import { Component, OnInit } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { GridReadyEvent } from "ag-grid-community";
import { ApiService } from "../../services";
import { DataTypes } from "./admin-view-data-types";
import { forkJoin } from "rxjs";
import {
  ArmResourceTypeColumnDefinitionForAdmin,
  AzColumnDefinitionForAdmin,
  BatchSkuColumnDefinitionForAdmin,
  HdiSkuColumnDefinitionForAdmin,
  ProjectColumnDefinitionForAdmin,
  RegionTableColumnDefinitionForAdmin,
  ServiceReadinessColumnDefinitionForAdmin,
  ServiceReadinessStageColumnDefinitionForAdmin,
  SqlColumnDefinitionForAdmin,
  VmDiskColumnDefinitionForAdmin,
  VmSkuColumnDefinitionForAdmin,
  PfSkuColumnDefinitionForAdmin,
} from "./ag-grid-column-definition";
import { IStatusResponse, TableViewDataInfo } from "src/app/npr-request.model";
import { AZ, ArmResourceType, HdiSku, RegionTable, ServiceReadiness, ServiceReadinessStage, Sql, VmSku } from "src/app/generated-models";
import { SharedDataService } from "../../services/sharedDataService";
import { VmDisk } from "../../generated-models/VmDisk";
import { BatchSku } from "src/app/generated-models/BatchSku";
import { Project } from "../../generated-models/Project";
import { PfSku } from "../../generated-models/PfSku";
import { AdminViewComponent } from "./admin-view.component";
import { ModalService } from "src/app/shared/modal.service";

interface AzTableRow extends AZ {
  DisplayRegionName: string;
}

@Component({
  standalone: false,
  selector: "app-admin-view-table-view",
  templateUrl: "./admin-view-table-view.component.html",
  styleUrls: ["../../styles.scss"],
})
export class AdminViewTableViewComponent extends AdminViewComponent<any> implements OnInit {
  type = "Table View";
  tableName = "AZ";
  columnDef: any = AzColumnDefinitionForAdmin;
  keyFilterStats = "filter:admin-view-table-view";

  tableList: TableViewDataInfo[] = [];
  tableItem: TableViewDataInfo;
  isAdmin = false;
  isEditMode = false;

  // Per-table operation support. A table absent from this map is fully read-only.
  // Tables whose columns are all part of the primary key get insert/delete but no edit.
  private readonly tableCapabilities: Record<string, { insert: boolean; edit: boolean; delete: boolean }> = {
    "AZ": { insert: true, edit: true, delete: true },
    "Service Readiness": { insert: true, edit: true, delete: true },
    "Sql": { insert: true, edit: true, delete: true },
    "Arm Resource Type": { insert: true, edit: true, delete: true },
    "Vm Sku": { insert: true, edit: true, delete: true },
    "Pf Sku": { insert: true, edit: true, delete: true },
    "Project": { insert: true, edit: true, delete: true },
    "Hdi Sku": { insert: true, edit: false, delete: true },
    "Batch Sku": { insert: true, edit: false, delete: true },
    "Service Readiness Stage": { insert: true, edit: false, delete: true },
  };

  // Fields the server owns or the UI derives, so they must not appear on the add-row form
  private readonly addRowExcludedFields: Record<string, Set<string>> = {
    "AZ": new Set(["DisplayRegionName"]),
    "Project": new Set(["CreatedBy", "CreatedTime"]),
    "Pf Sku": new Set(["LastUpdatedTime", "LastUpdatedBy"]),
    "Service Readiness Stage": new Set(["UpdatedTime"]),
  };

  // Map of table name to CRUD functions
  private tableAddFuncMap: Record<string, (data: any) => Promise<IStatusResponse>> = {};
  private tableUpdateFuncMap: Record<string, (data: any) => Promise<IStatusResponse>> = {};
  private tableDeleteFuncMap: Record<string, (data: any) => Promise<IStatusResponse>> = {};
  private tableRefreshFuncMap: Record<string, () => void> = {};

  constructor(
    apiService: ApiService,
    modalService: ModalService,
    notificationService: ToastrService,
    sharedDataService: SharedDataService,
    dataTypes: DataTypes
  ) {
    super(apiService, modalService, notificationService, sharedDataService, dataTypes);
    this.addRowEnable = false;
    this.editRowEnable = false;
    this.deleteRowEnable = false;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.isAdmin = ApiService.userProfile?.IsAdmin ?? false;
    this.initCrudFuncMaps();
    this.getTableList();
  }

  refreshData(): void {
    super.refreshData();
    const refreshFunc = this.tableRefreshFuncMap[this.tableItem?.TableName];
    if (refreshFunc) {
      refreshFunc();
    }
  }

  onGridReady(params: GridReadyEvent): void {
    super.onGridReady(params);
  }

  async addRow(): Promise<void> {
    const excluded = this.addRowExcludedFields[this.tableName];
    const addColumnDef = excluded ? this.columnDef.filter((col: { field: string }) => !excluded.has(col.field)) : this.columnDef;
    await this.addRowCommonProcess(addColumnDef);
  }

  toggleEditMode(): void {
    if (this.isEditMode) {
      this.isEditMode = false;
      this.onTableChanged();
      return;
    }

    this.modalService.informationModal(
      "DO <b>NOT</b> EDIT ANY TABLES WITHOUT EXPLICIT APPROVAL FROM ANOTHER ADMINISTRATOR!",
      "Warning"
    ).then(() => {
      this.isEditMode = true;
      this.onTableChanged();
    });
  }

  private initCrudFuncMaps(): void {
    this.tableAddFuncMap = {
      "AZ": (data: AZ) => this.apiService.createAzEntity(data).toPromise(),
      "Service Readiness": (data: ServiceReadiness) => this.apiService.createServiceReadiness(data).toPromise(),
      "Sql": (data: Sql) => this.apiService.createSql(data).toPromise(),
      "Arm Resource Type": (data: ArmResourceType) => this.apiService.createArmResourceType(data).toPromise(),
      "Vm Sku": (data: VmSku) => this.apiService.createVmSku(data).toPromise(),
      "Pf Sku": (data: PfSku) => this.apiService.createPfSku(data).toPromise(),
      "Project": (data: Project) => this.apiService.createProject(data).toPromise(),
      "Hdi Sku": (data: HdiSku) => this.apiService.createHdiSku(data).toPromise(),
      "Batch Sku": (data: BatchSku) => this.apiService.createBatchSku(data).toPromise(),
      "Service Readiness Stage": (data: ServiceReadinessStage) => this.apiService.createServiceReadinessStage(data).toPromise(),
    };

    this.tableUpdateFuncMap = {
      "AZ": (data: AZ) => this.apiService.updateAzEntity(data).toPromise(),
      "Service Readiness": (data: ServiceReadiness) => this.apiService.updateServiceReadiness(data).toPromise(),
      "Sql": (data: Sql) => this.apiService.updateSql(data).toPromise(),
      "Arm Resource Type": (data: ArmResourceType) => this.apiService.updateArmResourceType(data).toPromise(),
      "Vm Sku": (data: VmSku) => this.apiService.updateVmSku(data).toPromise(),
      "Pf Sku": (data: PfSku) => this.apiService.updatePfSku(data).toPromise(),
      "Project": (data: Project) => this.apiService.updateProject(data).toPromise(),
    };

    this.tableDeleteFuncMap = {
      "AZ": (data: AZ) => this.apiService.deleteAzEntity(data).toPromise(),
      "Service Readiness": (data: ServiceReadiness) => this.apiService.deleteServiceReadiness(data).toPromise(),
      "Sql": (data: Sql) => this.apiService.deleteSql(data).toPromise(),
      "Arm Resource Type": (data: ArmResourceType) => this.apiService.deleteArmResourceType(data).toPromise(),
      "Vm Sku": (data: VmSku) => this.apiService.deleteVmSku(data).toPromise(),
      "Pf Sku": (data: PfSku) => this.apiService.deletePfSku(data).toPromise(),
      "Project": (data: Project) => this.apiService.deleteProject(data).toPromise(),
      "Hdi Sku": (data: HdiSku) => this.apiService.deleteHdiSku(data).toPromise(),
      "Batch Sku": (data: BatchSku) => this.apiService.deleteBatchSku(data).toPromise(),
      "Service Readiness Stage": (data: ServiceReadinessStage) => this.apiService.deleteServiceReadinessStage(data).toPromise(),
    };
  }

  private initRefreshFuncMap(): void {
    this.tableRefreshFuncMap = {};
    for (const item of this.tableList) {
      this.tableRefreshFuncMap[item.TableName] = () => {
        // Re-fetch data for the selected table
        this.getTableDataByName(item.TableName);
      };
    }
  }

  private getTableDataByName(tableName: string): void {
    const fetchMap: Record<string, () => void> = {
      "AZ": () => this.apiService.getAzEntity(true).subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
      "Hdi Sku": () => this.apiService.getHdiSkus().subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
      "Service Readiness": () => this.apiService.getServiceReadinesses().subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
      "Service Readiness Stage": () => this.apiService.getServiceReadinessStages().subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
      "Sql": () => this.apiService.getSqls().subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
      "Arm Resource Type": () => this.apiService.getArmResourceTypes().subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
      "Virtual Machine Disk": () => this.apiService.getVmDisk().subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
      "Region": () => this.apiService.getRegions().subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
      "Batch Sku": () => this.apiService.getBatchSkus().subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
      "Vm Sku": () => this.apiService.getAllVmSkuEntities().subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
      "Pf Sku": () => this.apiService.getAllPfSkuEntities().subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
      "Project": () => this.apiService.getProjects().subscribe(data => { this.updateTableItemData(tableName, data); this.setRowData(data); }),
    };

    const fetchFunc = fetchMap[tableName];
    if (fetchFunc) {
      fetchFunc();
    }
  }

  private updateTableItemData(tableName: string, data: any[]): void {
    const item = this.tableList.find(t => t.TableName === tableName);
    if (item) {
      item.rowData = data;
    }
  }

  onTableChanged(): void {
    if (this.tableItem != null) {
      this.tableName = this.tableItem.TableName;
      this.columnDef = this.tableItem.ColumnDefs;
      this.gridOptions = { ...this.gridOptions, columnDefs: this.columnDef };

      // Update insert/edit/delete availability based on admin status, edit mode, and table
      const capabilities = this.tableCapabilities[this.tableName];
      const canModify = this.isAdmin && this.isEditMode;
      this.addRowEnable = canModify && (capabilities?.insert ?? false);
      this.editRowEnable = canModify && (capabilities?.edit ?? false);
      this.deleteRowEnable = canModify && (capabilities?.delete ?? false);

      // Wire CRUD functions for the selected table
      this.addRowFunc = this.tableAddFuncMap[this.tableName];
      this.updateRowFunc = this.tableUpdateFuncMap[this.tableName];
      this.deleteRowFunc = this.tableDeleteFuncMap[this.tableName];

      if (this.gridApi) {
        this.gridApi.setGridOption("columnDefs", this.columnDef);
      }

      // Reset selection
      this.selectedRow = null;
      this.selectedRowsCount = 0;
      this.message = "";

      // Use setRowData to update grid and trigger change detection,
      // ensuring the dropdown and row count update on initial load.
      this.setRowData(this.tableItem.rowData);
    }
  }

  async getTableList(): Promise<void> {
    forkJoin([
      this.apiService.getAzEntity(true),
      this.apiService.getHdiSkus(),
      this.apiService.getServiceReadinesses(),
      this.apiService.getServiceReadinessStages(),
      this.apiService.getSqls(),
      this.apiService.getArmResourceTypes(),
      this.apiService.getVmDisk(),
      this.apiService.getRegions(),
      this.apiService.getBatchSkus(),
      this.apiService.getAllVmSkuEntities(),
      this.apiService.getAllPfSkuEntities(),
      this.apiService.getProjects()
    ]).subscribe(
      ([
        azEntities,
        hdiSkuEntities,
        serviceReadinessEntities,
        serviceReadinessStageEntities,
        sqlEntities,
        armResourceTypeEntities,
        supportedVmDisks,
        regionEntities,
        batchSkuEntities,
        vmSkuEntities,
        pfSkuEntities,
        projectEntities,
      ]: [AZ[], HdiSku[], ServiceReadiness[], ServiceReadinessStage[], Sql[], ArmResourceType[], VmDisk[], RegionTable[], BatchSku[], VmSku[], PfSku[], Project[]]) => {
        const regionNameByDcmtRegionId = new Map(
          regionEntities.map((region) => [region.DCMTRegionId.toLowerCase(), region.RegionName])
        );
        const azRows: AzTableRow[] = azEntities.map((az) => ({
          ...az,
          DisplayRegionName: regionNameByDcmtRegionId.get(az.DCMTRegionId.toLowerCase()) ?? "",
        }));

        this.tableItem = {
          TableName: "AZ",
          TableTitle: "AZ",
          ColumnDefs: AzColumnDefinitionForAdmin,
          rowData: azRows,
        } as TableViewDataInfo;

        this.tableList = [
          this.tableItem,
          {
            TableName: "Hdi Sku",
            TableTitle: "HdiSku",
            ColumnDefs: HdiSkuColumnDefinitionForAdmin,
            rowData: hdiSkuEntities,
          },
          {
            TableName: "Service Readiness",
            TableTitle: "ServiceReadiness",
            ColumnDefs: ServiceReadinessColumnDefinitionForAdmin,
            rowData: serviceReadinessEntities,
          },
          {
            TableName: "Service Readiness Stage",
            TableTitle: "ServiceReadinessStage",
            ColumnDefs: ServiceReadinessStageColumnDefinitionForAdmin,
            rowData: serviceReadinessStageEntities,
          },
          {
            TableName: "Sql",
            TableTitle: "Sql",
            ColumnDefs: SqlColumnDefinitionForAdmin,
            rowData: sqlEntities,
          },
          {
            TableName: "Arm Resource Type",
            TableTitle: "ArmResourceType",
            ColumnDefs: ArmResourceTypeColumnDefinitionForAdmin,
            rowData: armResourceTypeEntities,
          },
          {
            TableName: "Virtual Machine Disk",
            TableTitle: "VmDisk",
            ColumnDefs: VmDiskColumnDefinitionForAdmin,
            rowData: supportedVmDisks,
          },
          {
            TableName: "Region",
            TableTitle: "Region",
            ColumnDefs: RegionTableColumnDefinitionForAdmin,
            rowData: regionEntities,
          },
          {
            TableName: "Batch Sku",
            TableTitle: "BatchSku",
            ColumnDefs: BatchSkuColumnDefinitionForAdmin,
            rowData: batchSkuEntities,
          },
          {
            TableName: "Vm Sku",
            TableTitle: "VmSku",
            ColumnDefs: VmSkuColumnDefinitionForAdmin,
            rowData: vmSkuEntities,
          },
          {
            TableName: "Pf Sku",
            TableTitle: "PfSku",
            ColumnDefs: PfSkuColumnDefinitionForAdmin,
            rowData: pfSkuEntities,
          },
          {
            TableName: "Project",
            TableTitle: "Project",
            ColumnDefs: ProjectColumnDefinitionForAdmin,
            rowData: projectEntities,
          }
        ];

        this.initRefreshFuncMap();
        this.onTableChanged();
      },
      (err: unknown) => {
        this.notificationService.error(err as string);
      }
    );
  }
}
